/**
 * "Salvar treinos no Health Connect" (`D281`): the profile's switch, and the
 * runtime that writes each finished workout while it is on.
 *
 * The row draws NOTHING where Health Connect cannot be used — the web, a
 * shell older than the plugin, a phone without Health Connect — and only a
 * line asking for an update where it is installed but too old. Turning it on
 * asks Health Connect's own permission sheet first; a refusal leaves it off
 * and says so. Turning it off only stops future writes: what was written
 * stays in Health Connect, which is the person's to manage there.
 */
import {
  "native-release.health_denied" as healthDenied,
  "native-release.health_hint" as healthHint,
  "native-release.health_label" as healthLabel,
  "native-release.health_update" as healthUpdate,
} from "@repo/i18n";
import { useCallback, useEffect, useId, useState } from "react";
import {
  type HealthConnectState,
  healthConnectState,
  preferences,
  requestHealthConnect,
  writeHealthWorkout,
} from "../../native";
import { Switch } from "../../ui/controls";
import { useToast } from "../../ui/overlay";
import { sessionTitle } from "../plan/session-name";
import { workoutStore } from "../workout/store/instance";
import { createHealthExport, HEALTH_EXPORT_KEY } from "./health-export";

const storage = {
  getItem: (key: string) => preferences.get(key),
  setItem: (key: string, value: string) => preferences.set(key, value),
};

/**
 * Starts writing finished workouts; returns the stop. Called once by
 * `<ConnectionsRuntime />`. With the switch off, or no Health Connect, each
 * ended session costs one Preferences read and nothing else.
 */
export function startHealthExport(): () => void {
  const exporter = createHealthExport({
    storage,
    titleOf: (session) =>
      session.label === null ? undefined : sessionTitle(session.label),
    write: writeHealthWorkout,
  });
  const run = () => {
    exporter.update(workoutStore.getState().session).catch((error: unknown) => {
      console.warn("[connections] Health Connect export failed", error);
    });
  };
  let unsubscribe: (() => void) | null = null;
  let stopped = false;
  workoutStore.hydration.whenHydrated().then(() => {
    if (stopped) {
      return;
    }
    run();
    unsubscribe = workoutStore.subscribe(run);
  });
  return () => {
    stopped = true;
    unsubscribe?.();
  };
}

/**
 * Where Health Connect stands and whether the switch is on; `null` until
 * known. Read by `<ConnectionsCard />`, which draws the row only when there
 * is something to switch.
 */
export function useHealthConnect(): {
  readonly on: boolean;
  readonly setOn: (on: boolean) => void;
  readonly setState: (state: HealthConnectState) => void;
  readonly state: HealthConnectState | null;
} {
  const [state, setState] = useState<HealthConnectState | null>(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    let live = true;
    Promise.all([healthConnectState(), preferences.get(HEALTH_EXPORT_KEY)])
      .then(([current, stored]) => {
        if (live) {
          setState(current);
          setOn(stored === "on" && current.granted);
        }
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  return { on, setOn, setState, state };
}

/** The switch, for a Health Connect that is installed (`state` known). */
export function HealthConnectRow({
  health,
}: {
  health: ReturnType<typeof useHealthConnect>;
}) {
  const { notify } = useToast();
  const { on, setOn, setState, state } = health;

  const change = useCallback(
    (next: boolean) => {
      if (!next) {
        setOn(false);
        preferences.remove(HEALTH_EXPORT_KEY).catch(() => undefined);
        return;
      }
      requestHealthConnect()
        .then(async (answer) => {
          setState(answer);
          if (!answer.granted) {
            notify({ message: healthDenied() });
            return;
          }
          await preferences.set(HEALTH_EXPORT_KEY, "on");
          setOn(true);
        })
        .catch(() => undefined);
    },
    [notify, setOn, setState]
  );

  if (state === null || state.status === "unavailable") {
    return null;
  }
  return (
    <HealthConnectRowView on={on} onChange={change} status={state.status} />
  );
}

/** What the row draws, from its state alone — the stories' door. */
export function HealthConnectRowView({
  on,
  onChange,
  status,
}: {
  on: boolean;
  onChange: (on: boolean) => void;
  status: "available" | "update-required";
}) {
  const id = useId();
  if (status === "update-required") {
    return (
      <div className="border-line border-t px-4 py-3">
        <p className="text-base text-ink">{healthLabel()}</p>
        <p className="mt-1 text-ink-3 text-sm">{healthUpdate()}</p>
      </div>
    );
  }
  return (
    <div className="flex min-h-16 items-center gap-3 border-line border-t px-4 py-3">
      {/* The label is the title alone: the hint inside it would become
          part of the switch's accessible name. */}
      <div className="min-w-0 flex-1">
        <label className="block text-base text-ink" htmlFor={id}>
          {healthLabel()}
        </label>
        <p className="mt-1 text-ink-3 text-sm">{healthHint()}</p>
      </div>
      <Switch
        checked={on}
        id={id}
        label={healthLabel()}
        onCheckedChange={onChange}
      />
    </div>
  );
}
